<template>
    <aside
        class="flex h-full w-62 flex-col border-r border-default bg-default"
    >
        <!-- Brand -->
        <div class="flex items-center gap-3 px-5 h-16 border-b border-default">
            <AtomLogo class="size-8 shrink-0" />
            <div class="leading-tight">
                <p class="text-sm font-semibold text-highlighted">{{ $t('app.name') }}</p>
                <p class="text-xs text-muted">{{ $t('app.institution') }}</p>
            </div>
        </div>

        <!-- Nav -->
        <nav class="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-4">
            <template v-for="(section, si) in visibleSections" :key="si">
                <div class="flex flex-col gap-1">
                    <p v-if="section.header" class="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-dimmed">
                        {{ $t(section.header) }}
                    </p>
                    <RouterLink
                        v-for="item in section.items"
                        :key="item.key"
                        :to="item.to"
                        class="group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                        :class="isActive(item.to)
                            ? 'bg-elevated text-highlighted'
                            : 'text-muted hover:bg-elevated/60 hover:text-default'"
                        @click="onNavigate"
                    >
                        <UIcon :name="item.icon" class="size-5 shrink-0"
                               :class="isActive(item.to) ? 'text-primary' : ''" />
                        <span class="flex-1">{{ $t(item.label) }}</span>
                        <UBadge v-if="item.badge && notificationStore.unread > 0"
                                color="error" size="sm" :label="String(notificationStore.unread)"
                                :ui="{ base: 'h-5 min-w-5 justify-center rounded-full px-1 font-semibold' }" />
                        <UBadge v-else-if="item.pendingBadge && notificationStore.pending > 0"
                                color="warning" size="sm" :label="String(notificationStore.pending)"
                                :ui="{ base: 'h-5 min-w-5 justify-center rounded-full px-1 font-semibold' }" />
                    </RouterLink>
                </div>
            </template>
        </nav>

        <!-- Footer profile link -->
        <RouterLink
            to="/profil"
            class="flex items-center gap-3 border-t border-default px-4 py-3 hover:bg-elevated/60 transition-colors"
            @click="onNavigate"
        >
            <UAvatar :text="initials" size="sm" />
            <div class="min-w-0 flex-1 leading-tight">
                <p class="truncate text-sm font-medium text-default">{{ fullName }}</p>
                <p class="truncate text-xs text-muted">{{ roleLabel }}</p>
            </div>
        </RouterLink>
    </aside>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useRoute } from 'vue-router'

    import AtomLogo from '@/layouts/components/AtomLogo.vue'
    import { navSections, canSeeItem, sidebarOpen } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'
    import { useNotificationStore } from '@/stores/notification'
    import { getUserRole, userInitials } from '@/libs/domain'

    const route = useRoute()
    const appStore = useAppStore()
    const notificationStore = useNotificationStore()

    const userRole = computed(() => appStore.userData?.role)

    const reportsGrant = computed(() => !!appStore.userData?.can_view_reports)

    // A section shows whenever at least one of its items is visible, so a user with only
    // the report grant still gets the "Upravljanje" header above "Poročila".
    const visibleSections = computed(() =>
        navSections
            .map((s) => ({
                ...s,
                items: s.items.filter((i) => canSeeItem(i, userRole.value, reportsGrant.value)),
            }))
            .filter((s) => s.items.length > 0),
    )

    const fullName = computed(() =>
        appStore.userData ? `${appStore.userData.first_name} ${appStore.userData.last_name}` : '',
    )
    const initials = computed(() => userInitials(fullName.value))
    const roleLabel = computed(() => (userRole.value ? getUserRole(userRole.value).label : ''))

    const isActive = (to: string) => route.path === to

    const onNavigate = () => {
        sidebarOpen.value = false
    }
</script>
